import React, { useEffect, useCallback, useMemo, useState } from 'react'
import useParams from 'core/hooks/useParams'
import useReactRouter from 'use-react-router'
import { routeManager } from 'core/utils/routes'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createNetwork, createSubnet, listNetworks } from '../actions'
import ModalForm from 'core/elements/modal/ModalForm'
import { Route } from 'core/plugins/route'
import TextField from 'core/components/validatedForm/TextField'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import ProviderNetworkTypePicklist from 'openstack/components/networks/ProviderNetworkTypePicklist'
import AllocationPoolsField from 'openstack/components/networks/AllocationPoolsField'
import DnsNameServersField from 'openstack/components/networks/DnsNameServersField'
import {
  cidrValidator,
  gatewayInCidrValidator,
  gatewayIpValid,
} from 'openstack/components/networks/SubnetFormFields'
import { customValidator } from 'core/utils/fieldValidators'
import useListAction from 'core/hooks/useListAction'
import AdminStatePicklist from '../AdminStatePicklist'
import { BasicSubnetFields, AdvancedSubnetFields } from '../SubnetFormFields'
import { MAKE_SHARED_INFO } from '../constants'

const useStyles = makeStyles<Theme>((theme) => ({}))

interface Props {
  addRoute: Route
}

export const getNetworkBody = ({ networkType, params }) => {
  if (networkType === 'flat') {
    return {
      network: {
        name: params.name,
        description: params.description,
        admin_state_up: params.adminState !== 'Down',
        'provider:network_type': params.networkType,
        'router:external': false,
        shared: true,
        port_security_enabled: params.enablePortSecurity,
      },
    }
  } else if (networkType === 'vlan') {
    return {
      network: {
        name: params.name,
        description: params.description,
        admin_state_up: params.adminState !== 'Down',
        'provider:network_type': params.networkType,
        'provider:segmentation_id': params.segmentationId,
        'router:external': false,
        shared: true,
        port_security_enabled: params.enablePortSecurity,
      },
    }
  }
  return {}
}

export default function CreateVmwareNetworkModal({ addRoute }: Props) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const [createdNetworkId, setCreatedNetworkId] = useState(null)
  const defaultParams = {
    networkType: 'tenant',
    externalNetworkType: 'flat',
    segmentationId: '',
    name: '',
    description: '',
    shared: false,
    adminState: 'Up',
    createSubnet: false,
    ipVersion: 4,
    cidr: '',
    gatewayIp: '',
    disableGateway: false,
    allocationPools: [],
    dnsNameServers: [],
    hostRoutes: [],
    enableDhcp: true,
    enablePortSecurity: true,
  }

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { reload } = useListAction(listNetworks, {})
  const { update, updating, error, reset } = useUpdateAction(createNetwork)
  const {
    update: updateSubnet,
    updating: updatingSubnet,
    error: subnetError,
    reset: resetSubnet,
  } = useUpdateAction(createSubnet)

  const createNetworkFn = async (params) => {
    const body = getNetworkBody({ networkType: params.networkType, params })
    const { success, response } = await update({ body })
    return { success, response }
  }

  const createSubnetFn = async (params, networkId) => {
    const allocationPools = params.allocationPools.map((entry) => {
      const [start, end] = entry.replace(/\s/g, '').split('-')
      return {
        start,
        end,
      }
    })
    const hostRoutes = params.hostRoutes.map((entry) => {
      const [cidr, nexthop] = entry.replace(/\s/g, '').split(',')
      return {
        destination: cidr,
        nexthop,
      }
    })
    const body = {
      subnet: {
        name: params.subnetName,
        network_id: networkId,
        ip_version: params.ipVersion,
        cidr: params.cidr,
        gateway_ip: params.disableGateway ? null : params.gatewayIp ? params.gatewayIp : undefined,
        allocation_pools: allocationPools.length ? allocationPools : undefined,
        dns_nameservers: params.dnsNameServers.length ? params.dnsNameServers : undefined,
        host_routes: hostRoutes.length ? hostRoutes : undefined,
        enable_dhcp: params.enableDhcp,
      },
    }
    const { success } = await updateSubnet({ body })
    return { success }
  }

  const submitForm = useCallback(async () => {
    if (createdNetworkId && params.createSubnet) {
      const { success } = await createSubnetFn(params, createdNetworkId)
      if (success) {
        reload(true, true)
        handleClose()
      }
    } else if (createdNetworkId && !params.createSubnet) {
      handleClose()
    } else {
      const { success, response } = await createNetworkFn(params)
      if (params.createSubnet) {
        const networkId = response?.id
        setCreatedNetworkId(networkId)
        const { success: subnetSuccess } = await createSubnetFn(params, networkId)
        if (subnetSuccess) {
          reload(true, true)
          handleClose()
        } else {
          return
        }
      }
      if (success) {
        handleClose()
      }
    }
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetSubnet()
    setCreatedNetworkId(null)
    history.push(routeManager.routes.openstack.vmwareNetworks.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title="Add Network"
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingSubnet}
      error={error || subnetError}
      submitTitle="Add Network"
    >
      <FormFieldSection title="Network Settings">
        <TextField
          id="name"
          label="Network Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          info="The name of the VM network in neutron"
          required
        />
        <TextField
          id="description"
          label="Description"
          onChange={getParamsUpdater('description')}
          value={params.description}
        />
        <AdminStatePicklist value={params.adminState} onChange={getParamsUpdater('adminState')} />
        <ProviderNetworkTypePicklist
          name="networkType"
          label="Network Type"
          value={params.networkType}
          noVxlan={true}
          onChange={getParamsUpdater('networkType')}
        />
        {['vlan', 'vxlan'].includes(params.networkType) && (
          <TextField
            id="segmentationId"
            label="VLAN ID"
            onChange={getParamsUpdater('segmentationId')}
            value={params.segmentationId}
            info="The segmentation ID of the network"
            required
          />
        )}
        <CheckboxField
          id="enablePortSecurity"
          label="Enable Port Security"
          value={params.enablePortSecurity}
          onChange={getParamsUpdater('enablePortSecurity')}
          info="If checked, newly created ports on this network will have port security enabled by default."
        />
        <CheckboxField
          id="shared"
          label="Make Shared"
          value={params.shared}
          onChange={getParamsUpdater('shared')}
          info={MAKE_SHARED_INFO}
        />
        <CheckboxField
          id="createSubnet"
          label="Create Subnet"
          value={params.createSubnet}
          onChange={getParamsUpdater('createSubnet')}
          info="Create a subnet for this network now."
        />
      </FormFieldSection>
      {params.createSubnet && (
        <>
          <BasicSubnetFields
            params={params}
            getParamsUpdater={getParamsUpdater}
            inNetworkCreation
          />
          <AdvancedSubnetFields params={params} updateParams={updateParams} />
        </>
      )}
    </ModalForm>
  )
}
